import { z } from 'zod';
import {
  amountSchema,
  createModel,
  partySchema,
  productPurchaseSchema,
  QueryMany,
  QueryOne,
  refSchema,
  uTagsSchema,
} from '../../common';
import {
  dateSchema,
  idSchema,
  nameSchema,
  objectIdSchema,
  priceSchema,
} from '../../validation';
import { billingSourceSchema } from './billing.source.model';
import { billingStatusSchema } from './billing.status.model';
import { billingItemSchema } from './billing.item.model';
import { subscriptionSnapshotSchema } from './billing.subscription-snapshot.model';
import { billingRefSchema } from '../sales';

/**
 * `Billing` — the Hero-side analog of `Invoice`.
 *
 * **Source-discriminated** so it grows beyond subscriptions without schema
 * changes. Today: subscription periods, addons. Tomorrow: professional
 * services, hardware sales, training — just add a `source` value.
 *
 * Commercial identity lives on {@link productPurchaseSchema} (`purchase`),
 * not as separate `product` / `sku` fields.
 *
 * Status flow:
 *   draft -> pending -> partially_paid -> paid
 *                                       \-> past_due
 *                                       \-> refunded
 *   draft -> cancelled
 */
export const BillingSchema = createModel({
  /** Human-readable ref (e.g. BIL-2026-03-000045). */
  ref: refSchema,
  /** Display title — e.g. "Growth Plan — March 2026". */
  title: nameSchema,

  /** What this Billing is for — the extensibility hinge. */
  source: billingSourceSchema,

  /**
   * What was sold. Required for subscription / addon Billings; absent only
   * for true `platform_manual` charges with no catalog tie.
   */
  purchase: productPurchaseSchema.optional(),

  /**
   * Subscription pointer + frozen pricing audit, in one field.
   * Absent for `platform_manual` / non-subscription Billings.
   */
  subscriptionSnapshot: subscriptionSnapshotSchema.optional(),
  /** Snapshot ref to a parent Billing — for credit / proration / refund chains. */
  parentBilling: billingRefSchema.optional(),

  payer: partySchema,

  leadId: objectIdSchema.optional(),

  /** Monetary breakdown (currency, total, paid, remaining, tax, etc.). */
  amount: amountSchema,

  /** Line items. Immutable after finalize. */
  items: z.array(billingItemSchema).min(1),

  /** Period covered (present for subscription / recurring sources). */
  period: z.object({ from: dateSchema, to: dateSchema }).optional(),

  /** Lifecycle. */
  status: billingStatusSchema,

  checkout: z
    .object({
      provider: z.enum(['moyasar']),
      type: z.enum(['invoice']),
      id: idSchema,
      url: z.string().url(),
    })
    .optional(),

  issuedAt: dateSchema.optional(),
  dueDate: dateSchema.optional(),
  paidAt: dateSchema.optional(),

  uTags: uTagsSchema,

  refundedAmount: priceSchema.default(0),
});

export type Billing = z.infer<typeof BillingSchema.model>;
export type BillingData = z.infer<typeof BillingSchema.data>;
export type BillingPartial = z.infer<typeof BillingSchema.partial>;
export type BillingQueryMany = QueryMany<Billing>;
export type BillingQueryOne = QueryOne<Billing>;
